import { Core, Decorative } from "@faire/design-tokens";
import { Carousel } from "@faire/retailer-visitor-shared/components/Carousel/Carousel";
import { PaddedContainer } from "@faire/retailer-visitor-shared/features/Marketplace/style";
import { getSessionCountry } from "@faire/retailer-visitor-shared/serialized-data/getSessionCountry";
import { THREE_LETTER_PRIMARY_COUNTRY_CODES } from "@faire/web--source/common/consts/THREE_LETTER_PRIMARY_COUNTRY_CODES";
import { optimizeImageUrlFillSquare } from "@faire/web--source/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Column, Flex, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import React from "react";
import styled from "styled-components";

export const LoggedOutSuppliersPageTestimonialBanner: React.FC = () => {
  const sessionCountry = getSessionCountry();
  const isUSA = sessionCountry === THREE_LETTER_PRIMARY_COUNTRY_CODES.USA;
  const testimonials = useTestimonials(isUSA);
  const { isTabletAndBelow } = useViewport();

  return (
    <StyledPaddedContainer $noPaddingOnMobile>
      <Wrapper>
        <RowToCol>
          <LoggedOutSuppliersPageHeader
            variant={{
              mobileAndAbove: "displaySSerifRegular",
              desktopAndAbove: "displayMSerifRegular",
            }}
          >
            <StrictLocalMsg
              defaultMessage="Join hundreds of thousands of retailers who shop on Faire"
              description={{
                text: "Testimonial header",
                img: "https://cdn.faire.com/fastly/c7c76c4b47ba43a10afc34184a1e6e311e328fea8a339cd252cee9a42a70db4b.png",
              }}
            />
          </LoggedOutSuppliersPageHeader>
          <Spacer size={{ mobileAndAbove: "3x", desktopAndAbove: 120 }} />
          <Carousel
            name="logged-out-suppliers-page-testimonials"
            itemsPerSlide={1}
            renderItem={(testimonial, index) => (
              <React.Fragment key={`${testimonial.reviewerName}-${index}`}>
                <Row>
                  <Spacer
                    width={{
                      mobileAndAbove: 0,
                      desktopAndAbove: "3x",
                    }}
                  />
                  <TestimonialTile
                    avatarImageUrl={testimonial.avatarImageUrl}
                    content={testimonial.content}
                    reviewerName={testimonial.reviewerName}
                    reviewerInfo={testimonial.reviewerInfo}
                  />
                  <Spacer
                    width={{
                      mobileAndAbove: 0,
                      desktopAndAbove: "3x",
                    }}
                  />
                </Row>
              </React.Fragment>
            )}
            shouldShowDots={isTabletAndBelow}
            items={testimonials}
            useHorizontalScrollCarousel={isTabletAndBelow}
          />
        </RowToCol>
      </Wrapper>
    </StyledPaddedContainer>
  );
};

interface ITestimonial {
  avatarImageUrl: string;
  content: React.ReactNode;
  mobileContent?: React.ReactNode;
  reviewerName: string;
  reviewerInfo: string;
}

const TestimonialTile: React.FC<ITestimonial> = ({
  avatarImageUrl,
  content,
  reviewerName,
  reviewerInfo,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const isSSR = useIsSSR();
  const image = IImage.build({
    url: optimizeImageUrlFillSquare({ url: avatarImageUrl }, isSSR, 40),
  });
  return (
    <TileWrapper>
      <Typography variant="subheadingSansRegular">{content}</Typography>
      <Spacer height="3x" />
      <CenterItemsRow>
        <AvatarImage
          src={image.url}
          alt={strictLocalize({
            defaultMessage: "testimonial retailer profile image",
            description: {
              text: "image showing the retailer of the testimonial",
              img: "https://cdn.faire.com/fastly/743f2eeae9709379df2b236c106752e0f9e245943ef33371b87e49910a3ca6ef.png",
            },
          })}
        />
        <Spacer width="2x" />
        <Column>
          <Typography>{reviewerName}</Typography>
          <Typography color={Core.text.subdued}>{reviewerInfo}</Typography>
        </Column>
      </CenterItemsRow>
    </TileWrapper>
  );
};

const useTestimonials = (isUSA: boolean): ITestimonial[] => {
  const { strictLocalize } = useStrictLocalization();
  return [
    {
      avatarImageUrl:
        "https://cdn.faire.com/fastly/72f810f95c17f7e377a96d5fa37775a7552c47cfd0cf40074a639b4d80803e05.png",
      content: (
        <StrictLocalMsg defaultMessage="“If you aren’t using Faire, you’re missing a great opportunity. Start today and deal with a very professional company helping all shopkeepers buy quality products from independent makers and artisans.”" />
      ),
      reviewerName: "Cindy Davenport",
      reviewerInfo: isUSA
        ? strictLocalize({
            defaultMessage: "Cinport Floral Studio & Gift Designs by Cinport",
            description: {
              text: "Testimonial retailer info",
              img: "https://cdn.faire.com/fastly/743f2eeae9709379df2b236c106752e0f9e245943ef33371b87e49910a3ca6ef.png",
            },
          })
        : strictLocalize({
            defaultMessage:
              "Owner of Floral Studio & Gift Designs by Cinport in Florida, USA",
            description: {
              text: "Testimonial retailer info",
              img: "https://cdn.faire.com/fastly/743f2eeae9709379df2b236c106752e0f9e245943ef33371b87e49910a3ca6ef.png",
            },
          }),
    },
    {
      avatarImageUrl:
        "https://cdn.faire.com/fastly/9cd7f99ab4862a8332e44f3876063e11cdf0a37efa2f634b3bc5b7c8a0fc3ca0.png",
      content: (
        <StrictLocalMsg defaultMessage="“As our business grows, Faire has allowed us to easily try products that we might not have taken the chance on before. The fact that they’ve tailored the selections to fit our brand has simplified our buying processes significantly.”" />
      ),
      mobileContent: (
        <StrictLocalMsg defaultMessage="“As our business grows, Faire has allowed us to easily try products that we might not have taken the chance on before.”" />
      ),
      reviewerName: strictLocalize({
        defaultMessage: "Jake France + Richard Rolfe",
        description: {
          text: "Testimonial retailer names",
          img: "https://cdn.faire.com/fastly/750d6f518ea4333b7c1fff35051630d0a622e6a43212be3ae49562b4a24c1f3d.png",
        },
      }),
      reviewerInfo: isUSA
        ? strictLocalize({
            defaultMessage: "Boys Fort",
            description: {
              text: "Testimonial retailer info",
              img: "https://cdn.faire.com/fastly/750d6f518ea4333b7c1fff35051630d0a622e6a43212be3ae49562b4a24c1f3d.png",
            },
          })
        : strictLocalize({
            defaultMessage: "Owners of Boys Fort in Portland, USA",
            description: {
              text: "Testimonial retailer info",
              img: "https://cdn.faire.com/fastly/750d6f518ea4333b7c1fff35051630d0a622e6a43212be3ae49562b4a24c1f3d.png",
            },
          }),
    },
    {
      avatarImageUrl:
        "https://cdn.faire.com/fastly/0ded13b33fd5d5c505deb0bd98ec2fb3bc283d2fb09d10ebd82995c4bd326df9.jpeg",
      content: (
        <StrictLocalMsg defaultMessage="“I love Faire because I don’t have to go to trade shows. Faire’s website is 100% more user-friendly and better designed than other wholesale sites.”" />
      ),
      reviewerName: strictLocalize({ defaultMessage: "John Frechette" }),
      reviewerInfo: isUSA
        ? "MADE"
        : strictLocalize({
            defaultMessage: "Owner of MADE in Jackson, USA",
            description: {
              text: "Testimonial retailer info",
              img: "https://cdn.faire.com/fastly/d254c664d36bd5c7228043923fa1099b02e8a967967bfdbb99075e184aa100e8.png",
            },
          }),
    },
  ];
};

const TileWrapper = styled(Column)`
  height: 100%;
`;

const CenterItemsRow = styled(Row).attrs({ align: "center" })``;

const AvatarImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 100%;
`;

const Wrapper = styled(Column)`
  width: 100%;
  background-color: ${Decorative.tint.neutral};
  padding: ${getSpacing("6x")};
  ${responsiveMediaQueries.tablet} {
    padding: ${getSpacing("6x", "3x")};
  }
  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("6x", "2x")};
  }
  margin-bottom: ${getSpacing("6x")};
`;

const RowToCol = styled(Flex).attrs({
  align: "center",
  direction: { mobileAndAbove: "column", desktopAndAbove: "row" },
})``;

const LoggedOutSuppliersPageHeader = styled(Typography)`
  min-width: 376px;
  ${responsiveMediaQueries.tabletAndBelow} {
    min-width: auto;
  }
`;

const StyledPaddedContainer = styled(PaddedContainer)`
  ${responsiveMediaQueries.tablet} {
    padding: ${getSpacing(0, "8x")};
  }
`;
